import * as React from 'react';
import { Component } from 'react';
import { computed, reaction } from 'mobx';
import { observer, Provider } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import _ShareclassRows from './ShareclassRows';
import _ShareclassCards from './ShareclassCards';
import withShareclass from './withShareclass';
import Column from './Column';
import GroupedColumn from './GroupedColumn';
import { Table, TableHead, TableBody, TableRow, ColumnHeader } from './Grid';
import Translate from './Translate';
import ColumnHeaderExpander from './ColumnHeaderExpander';
import FundGroupHeader from './FundGroupHeader';
import { getColumnLabel } from '../utils/getColumnLabel';
import {
    DataStore,
    EntityGroupStore,
    FilterStore,
    ScreenSizeStore,
} from '../stores';
import { ShareClass } from '../models/entity';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { SubGroupedFund } from '../models/groupedFunds';
import { camelCase } from 'change-case';
import { ChildReferences } from '../utils/accessibility/ChildReferences';
import { isNullOrUndefined } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';

const ShareclassRows = withShareclass(_ShareclassRows);
const ShareclassCards = withShareclass(_ShareclassCards);

const FundListWrapper = styled('div', 'fundListWrapper', 'fund')``;

const FundGroupWrapper = styled('section', 'fundGroupWrapper', 'fund')``;

const Cards = styled<any, any>('div', 'cards', 'fund')`
    ${propStyles};
`;

Cards.defaultProps = {};

const ColumnHeaderInner = styled<any, any>('div', 'columnHeaderInner', 'fund')`
    ${propStyles};
`;

ColumnHeaderInner.defaultProps = {
    display: 'flex',
    alignItems: 'center',
};

const ColumnLabel = styled<any, any>('div', 'columnLabel', 'fund')`
    ${propStyles};
`;

ColumnLabel.defaultProps = {
    flex: '1 1 auto',
    textAlign: 'center',
};

interface FundProps {
    theme?: { [key: string]: React.CSSProperties };
    dataStore?: DataStore;
    filterStore?: FilterStore;
    screenSizeStore?: ScreenSizeStore;
    shareClasses?: Array<ShareClass>;
    columns: Array<ColumnConfig>;
    options: EntityGroupOptions;
    fundGroupChildReferences: ChildReferences<HTMLElement>;
    fundGroupIndex: number;
}

interface EntityGroupOptions {
    grouped: boolean;
    parentFundName?: string;
    fundName: string;
    isSubFund: boolean;
    subGroupedFund: SubGroupedFund;
}

@inject('dataStore', 'filterStore', 'screenSizeStore')
@observer
class Fund extends Component<FundProps, any> {
    entityGroupStore: EntityGroupStore;

    constructor(props: FundProps) {
        super(props);

        this.entityGroupStore = new EntityGroupStore(
            {
                data: props.dataStore,
                filter: props.filterStore,
            },
            props.shareClasses,
            props.options
        );

        this.entityGroupStore.setDocumentDownloads();

        reaction(
            () => [
                props.dataStore.groupedFunds,
                props.dataStore.documentPropertySearch,
                props.dataStore.documentFilterSearch,
                props.dataStore.documentFilterInferredSearch,
            ],
            data => {
                this.entityGroupStore.setDocumentDownloads();
            },
            // TODO: Find the correct solution
            // Without a delay setDocumentDownloads seems to use the previous state
            // E.g. this.shareClasses is the previous group of share classes
            { delay: 1 }
        );
    }

    componentDidUpdate(prevProps: Readonly<FundProps>): void {
        if (prevProps.shareClasses !== this.props.shareClasses) {
            this.entityGroupStore.entities = this.props.shareClasses;
        }
    }

    @computed
    get rowExpanderConfig() {
        const { expander } = this.props.dataStore.config.rowConfig;
        return expander;
    }

    @computed
    get hasDropdownOrGroup() {
        const { columns } = this.props;
        return columns.some(column =>
            ['dropdown', 'group'].includes(column.type)
        );
    }

    @computed
    get rowSpan() {
        return this.hasDropdownOrGroup ? 2 : 1;
    }

    @computed
    get screenSizeStore(): ScreenSizeStore {
        return this.props?.screenSizeStore;
    }

    @computed
    get showTableView(): boolean {
        const { dataStore } = this.props;
        const cardViewBreakpoint =
            (dataStore && dataStore.switchToCardView) || 'md';
        return this.screenSizeStore.compareBreakpoints(
            this.screenSizeStore.breakpointSize,
            '>',
            cardViewBreakpoint
        );
    }

    @computed
    get metricId() {
        const { entityGroupStore } = this;
        const {
            fundName,
            parentFundName,
            isSubFund,
        } = entityGroupStore.options;
        return isSubFund
            ? `fundHeader-${camelCase(
                  parentFundName
              )}-fundSubHeader-${camelCase(fundName)}`
            : `fundHeader-${camelCase(fundName)}`;
    }

    @computed
    get tableHeadStickyConfig() {
        const { stickyConfig } = this.props.screenSizeStore;
        return stickyConfig?.tableHeader;
    }

    @computed
    get moduleExpandedHeader() {
        const { stickyConfig } = this.props.screenSizeStore;
        return stickyConfig?.moduleExpandedHeader;
    }

    @computed
    get stickyOffset() {
        const { isFiltersOpen } = this.props.filterStore;
        const {
            filterConfig,
        } = this.props.dataStore?.config.selectedModuleConfig;
        let offset = this.tableHeadStickyConfig?.offset;

        if (
            filterConfig?.top &&
            isFiltersOpen &&
            this.moduleExpandedHeader &&
            this.moduleExpandedHeader.enabled &&
            this.moduleExpandedHeader.offset &&
            !isNullOrUndefined(offset)
        ) {
            offset = offset + this.moduleExpandedHeader.offset;
        }
        return `${offset}px`;
    }

    @computed
    get stickyPosition() {
        return this.tableHeadStickyConfig?.enabled ? 'sticky' : 'static';
    }

    render() {
        const {
            rowExpanderConfig,
            showTableView,
            rowSpan,
            hasDropdownOrGroup,
            entityGroupStore,
            metricId,
        } = this;
        const {
            theme,
            dataStore,
            columns,
            fundGroupChildReferences,
            fundGroupIndex,
            children,
        } = this.props;
        const {
            fundName,
            parentFundName,
            grouped,
            isSubFund,
            subGroupedFund,
        } = entityGroupStore.options;
        return (
            <Provider entityGroupStore={entityGroupStore}>
                <FundListWrapper>
                    {grouped && (
                        <FundGroupHeader
                            fundName={fundName}
                            parentFundName={parentFundName}
                            metricId={metricId}
                            isSubFund={isSubFund}
                            subGroupedFund={subGroupedFund}
                            fundGroupChildReferences={fundGroupChildReferences}
                            fundGroupIndex={fundGroupIndex}
                            instanceListener={fundGroupChildReferences?.getChildIndexListener(
                                fundGroupIndex
                            )}
                        />
                    )}
                    <FundGroupWrapper aria-labelledby={metricId}>
                        {!children &&
                            entityGroupStore.expanded &&
                            (showTableView ? (
                                <Table
                                    aria-rowcount={
                                        entityGroupStore.shareClasses.length
                                    }
                                    {...theme.FundListTable}
                                >
                                    <TableHead
                                        position={this.stickyPosition}
                                        top={this.stickyOffset}
                                        zIndex={10}
                                        bgColor={'white'}
                                        {...{
                                            ...theme.ColumnList,
                                            ...theme.FundListTableHead,
                                        }}
                                    >
                                        <TableRow
                                            {...theme.FundListTableHeadRow}
                                        >
                                            {entityGroupStore.rowHasExpandableOptions &&
                                                (!rowExpanderConfig ||
                                                    rowExpanderConfig.align ===
                                                        'left') && (
                                                    <ColumnHeaderExpander
                                                        rowSpan={rowSpan}
                                                        showColumnHeader={
                                                            rowExpanderConfig &&
                                                            rowExpanderConfig.showColumnHeader
                                                        }
                                                    />
                                                )}
                                            {columns.map((column, index) => {
                                                const columnLabel = getColumnLabel(
                                                    column,
                                                    dataStore
                                                );
                                                if (
                                                    column.type ===
                                                        'watchlist' &&
                                                    dataStore.watchListEnabled
                                                ) {
                                                    return (
                                                        <ColumnHeader
                                                            rowSpan={rowSpan}
                                                            key={`${columnLabel}-${index}`}
                                                            paddingX="0"
                                                            {...theme.ColumnHeader}
                                                            width={
                                                                column.width ||
                                                                '50px'
                                                            }
                                                            verticalAlign={
                                                                column.verticalAlign
                                                            }
                                                            textAlign={
                                                                column.textAlign ||
                                                                'center'
                                                            }
                                                        >
                                                            {column.showLabel !==
                                                                false && (
                                                                <ColumnHeaderInner
                                                                    {...theme.ColumnHeaderInner}
                                                                >
                                                                    <ColumnLabel
                                                                        {...theme.ColumnLabel}
                                                                        textAlign={
                                                                            column.textAlign ||
                                                                            'center'
                                                                        }
                                                                    >
                                                                        <Translate>
                                                                            {
                                                                                columnLabel
                                                                            }
                                                                        </Translate>
                                                                    </ColumnLabel>
                                                                </ColumnHeaderInner>
                                                            )}
                                                        </ColumnHeader>
                                                    );
                                                }

                                                if (
                                                    [
                                                        'group',
                                                        'dropdown',
                                                    ].includes(column.type)
                                                ) {
                                                    return (
                                                        <GroupedColumn
                                                            key={`${columnLabel}-${index}`}
                                                            column={column}
                                                            columnIndex={index}
                                                            groupedColumnType="GroupedHeader"
                                                        />
                                                    );
                                                }

                                                return (
                                                    <Column
                                                        key={`${columnLabel}-${index}`}
                                                        column={column}
                                                        rowSpan={rowSpan}
                                                    />
                                                );
                                            })}
                                            {entityGroupStore.rowHasExpandableOptions &&
                                                rowExpanderConfig &&
                                                rowExpanderConfig.align ===
                                                    'right' && (
                                                    <ColumnHeaderExpander
                                                        rowSpan={rowSpan}
                                                        showColumnHeader={
                                                            rowExpanderConfig &&
                                                            rowExpanderConfig.showColumnHeader
                                                        }
                                                    />
                                                )}
                                        </TableRow>
                                        {hasDropdownOrGroup && (
                                            <TableRow
                                                {...theme.FundListTableHeadSubRow}
                                            >
                                                {columns.map(
                                                    (column, index) => {
                                                        const columnLabel = getColumnLabel(
                                                            column,
                                                            dataStore
                                                        );
                                                        if (
                                                            [
                                                                'group',
                                                                'dropdown',
                                                            ].includes(
                                                                column.type
                                                            )
                                                        ) {
                                                            return (
                                                                <GroupedColumn
                                                                    key={`${columnLabel}-${index}`}
                                                                    column={
                                                                        column
                                                                    }
                                                                    columnIndex={
                                                                        index
                                                                    }
                                                                    groupedColumnType="GroupedSubHeader"
                                                                />
                                                            );
                                                        }
                                                    }
                                                )}
                                            </TableRow>
                                        )}
                                    </TableHead>
                                    <TableBody {...theme.FundListTableBody}>
                                        {entityGroupStore.shareClasses.map(
                                            (shareClass, index) => (
                                                <ShareclassRows
                                                    key={`row-${dataStore.config.currentModuleId}-${index}`}
                                                    shareClass={shareClass}
                                                    columns={columns}
                                                    alternated={
                                                        index % 2 ? true : false
                                                    }
                                                    rowIndex={index + 1}
                                                />
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Cards>
                                    {entityGroupStore.shareClasses.map(
                                        (shareClass, index) => (
                                            <ShareclassCards
                                                key={`card-${index}`}
                                                shareClass={shareClass}
                                                columns={columns}
                                                alternated={
                                                    index % 2 ? true : false
                                                }
                                                rowIndex={index + 1}
                                            />
                                        )
                                    )}
                                </Cards>
                            ))}
                        {entityGroupStore.expanded && children}
                    </FundGroupWrapper>
                </FundListWrapper>
            </Provider>
        );
    }
}

export default withTheme(Fund) as any;



// WEBPACK FOOTER //
// ./src/Components/Fund.tsx